import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";
import { useDrop } from "react-dnd";
import { Pause, Play, SkipBack, SkipForward, X, Loader2 } from "lucide-react";
import clsx from "clsx";
import toast from "react-hot-toast";

import type { PlaybackFile } from "../../types";
import { PlaybackPlayer } from "../Player/PlaybackPlayer";
import { searchRecordings, startPlayback, stopPlayback } from "../../api/playback";
import { toLocalIso, playbackEndFor } from "../../lib/playbackTime";
import {
  transcodeKey as makeTranscodeKey, readTranscodeFlag, writeTranscodeFlag, clearTranscodeFlag,
} from "../../lib/transcodeFlag";
import { usePlaybackGridStore } from "../../store/playbackGridStore";

export interface PlaybackCellHandle {
  seek: (iso: string) => void;
  setPaused: (paused: boolean) => void;
  stop: () => void;
}

interface Props {
  cellId: string;
  deviceId: number | null;
  channel: number | null;
  channelName?: string | null;
  date: string; // yyyy-MM-dd
  rate: number;
  focused: boolean;
  showControls: boolean; // controles propios (modo independiente)
  onFocus: (cellId: string) => void;
  onFilesChange: (cellId: string, files: PlaybackFile[]) => void;
  onCursorChange: (cellId: string, iso: string | null) => void;
}

export const PlaybackCell = forwardRef<PlaybackCellHandle, Props>(function PlaybackCell(
  {
    cellId, deviceId, channel, channelName, date, rate, focused, showControls,
    onFocus, onFilesChange, onCursorChange,
  },
  ref,
) {
  const assignChannel = usePlaybackGridStore((s) => s.assignChannel);
  const clearCell = usePlaybackGridStore((s) => s.clearCell);

  const [files, setFiles] = useState<PlaybackFile[]>([]);
  const [playbackId, setPlaybackId] = useState<string | null>(null);
  const [streamName, setStreamName] = useState<string | null>(null);
  const [cursorIso, setCursorIso] = useState<string | null>(null);
  const [startedAtIso, setStartedAtIso] = useState<string | null>(null);
  const [paused, setPausedState] = useState(false);
  const [searching, setSearching] = useState(false);
  // `starting`: negociando el playback (entre el pedido y el primer frame). Evita
  // mostrar un cartel "vacio" mientras arranca; el overlay muestra "Conectando...".
  const [starting, setStarting] = useState(false);
  // Clave del ultimo (device|channel|date) auto-reproducido, para auto-play una
  // sola vez por asignacion (no re-arrancar tras Detener).
  const autoPlayedRef = useRef<string>("");

  const transcodeKey = makeTranscodeKey(deviceId, channel);
  const [transcode, setTranscode] = useState<boolean>(() => readTranscodeFlag(transcodeKey));
  useEffect(() => { setTranscode(readTranscodeFlag(transcodeKey)); }, [transcodeKey]);

  // Refs para que los metodos imperativos / callbacks lean valores frescos.
  const filesRef = useRef(files); filesRef.current = files;
  const transcodeRef = useRef(transcode); transcodeRef.current = transcode;
  const cursorRef = useRef(cursorIso); cursorRef.current = cursorIso;
  const startedAtRef = useRef(startedAtIso); startedAtRef.current = startedAtIso;
  const playbackIdRef = useRef(playbackId); playbackIdRef.current = playbackId;
  const dateRef = useRef(date); dateRef.current = date;

  // Reportar files al padre (alimenta el timeline de la celda enfocada).
  useEffect(() => { onFilesChange(cellId, files); }, [files, cellId, onFilesChange]);
  // Reportar cursor SOLO si esta celda esta enfocada: el timeline compartido solo
  // muestra la enfocada, y con 8 celdas reproduciendo a ~4Hz reportar todas haria
  // re-render del padre decenas de veces por segundo. Al cambiar el foco, la nueva
  // celda re-reporta su cursor actual (focused esta en deps).
  useEffect(() => {
    if (focused) onCursorChange(cellId, cursorIso);
  }, [cursorIso, focused, cellId, onCursorChange]);

  // Drop target: arrastrar un canal a esta celda.
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: "CHANNEL",
      collect: (m) => ({ isOver: m.isOver(), canDrop: m.canDrop() }),
      drop: (item: any) => {
        if (item?.type === "CHANNEL") assignChannel(cellId, item.deviceId, item.channel);
      },
    }),
    [cellId, assignChannel],
  );

  // Iniciar playback en un ISO. Reusa el patron probado del single-cam:
  // detener el stream anterior (fire-and-forget) y abrir el nuevo acotando `end`.
  const doSeek = useCallback((iso: string) => {
    if (deviceId == null || channel == null) return;
    const prevId = playbackIdRef.current;
    setPlaybackId(null);
    setStreamName(null);
    if (prevId) stopPlayback(prevId).catch(() => {});
    setCursorIso(iso);
    setStartedAtIso(iso);
    setPausedState(false);
    setStarting(true);
    const dayEnd = toLocalIso(new Date(dateRef.current + "T23:59:59"));
    const end = playbackEndFor(iso, filesRef.current, dayEnd);
    startPlayback({ device_id: deviceId, channel, start: iso, end, transcode: transcodeRef.current })
      .then((res) => { setPlaybackId(res.playback_id); setStreamName(res.stream_name); })
      .catch((e: any) => toast.error(e?.response?.data?.detail || `CH${channel}: no se pudo iniciar`))
      .finally(() => setStarting(false));
  }, [deviceId, channel]);

  const reseek = useCallback((nextTranscode: boolean) => {
    const iso = cursorRef.current;
    const prevId = playbackIdRef.current;
    if (prevId) stopPlayback(prevId).catch(() => {});
    setPlaybackId(null);
    setStreamName(null);
    if (!iso || deviceId == null || channel == null) return;
    setStarting(true);
    const dayEnd = toLocalIso(new Date(dateRef.current + "T23:59:59"));
    const end = playbackEndFor(iso, filesRef.current, dayEnd);
    startPlayback({ device_id: deviceId, channel, start: iso, end, transcode: nextTranscode })
      .then((res) => { setPlaybackId(res.playback_id); setStreamName(res.stream_name); })
      .catch((e: any) => toast.error(e?.response?.data?.detail || `CH${channel}: no se pudo iniciar`))
      .finally(() => setStarting(false));
  }, [deviceId, channel]);

  // Buscar grabaciones del dia al cambiar (device, channel, date). Reset total.
  useEffect(() => {
    if (playbackIdRef.current) stopPlayback(playbackIdRef.current).catch(() => {});
    setPlaybackId(null);
    setStreamName(null);
    setCursorIso(null);
    setStartedAtIso(null);
    setPausedState(false);
    setStarting(false);
    setFiles([]);
    autoPlayedRef.current = "";
    if (deviceId == null || channel == null) return;
    let cancelled = false;
    setSearching(true);
    const startD = new Date(date + "T00:00:00");
    const endD = new Date(date + "T23:59:59");
    searchRecordings({ device_id: deviceId, channel, start: toLocalIso(startD), end: toLocalIso(endD) })
      .then((res) => { if (!cancelled) setFiles(res.files); })
      .catch(() => { if (!cancelled) setFiles([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, channel, date]);

  // Auto-reproducir al cargar las grabaciones del dia: arranca desde el primer
  // tramo disponible (una sola vez por asignacion). Da feedback inmediato sin
  // pedir clic en Play. En Sincronizado, "Reproducir" arriba realinea todas a la
  // misma hora.
  useEffect(() => {
    if (deviceId == null || channel == null || searching) return;
    if (files.length === 0) return;
    if (playbackIdRef.current || starting) return;
    const key = `${deviceId}|${channel}|${date}`;
    if (autoPlayedRef.current === key) return;
    autoPlayedRef.current = key;
    doSeek(files[0].start);
  }, [files, searching, starting, deviceId, channel, date, doSeek]);

  // Cleanup al desmontar: detener el stream activo.
  useEffect(() => {
    return () => { if (playbackIdRef.current) stopPlayback(playbackIdRef.current).catch(() => {}); };
  }, []);

  useImperativeHandle(ref, () => ({
    seek: (iso: string) => doSeek(iso),
    setPaused: (p: boolean) => setPausedState(p),
    stop: () => {
      const id = playbackIdRef.current;
      if (id) stopPlayback(id).catch(() => {});
      setPlaybackId(null);
      setStreamName(null);
    },
  }), [doSeek]);

  // Sincroniza el cursor del timeline con el avance real del video.
  const onTimeUpdate = useCallback((elapsedSeconds: number) => {
    const started = startedAtRef.current;
    if (!started) return;
    const startMs = new Date(started).getTime();
    if (isNaN(startMs)) return;
    setCursorIso(toLocalIso(new Date(startMs + elapsedSeconds * 1000)));
  }, []);

  // Auto-advance al siguiente archivo del dia cuando el stream termina.
  const onStreamEnded = useCallback(() => {
    const cur = cursorRef.current;
    const fs = filesRef.current;
    if (fs.length === 0 || !cur) return;
    const curMs = new Date(cur).getTime();
    const next = fs.find((f) => new Date(f.start).getTime() > curMs);
    if (next) doSeek(next.start);
  }, [doSeek]);

  const onDecodeError = () => {
    if (transcodeRef.current) return;
    writeTranscodeFlag(transcodeKey);
    setTranscode(true);
    reseek(true);
  };
  const onTranscodeFailed = () => {
    if (!transcodeRef.current) return;
    clearTranscodeFlag(transcodeKey);
    setTranscode(false);
    reseek(false);
  };

  // Controles propios (modo independiente): play desde cursor/primer tramo, pausa, +-10s.
  const playLocal = () => {
    const iso = cursorRef.current
      ?? (filesRef.current[0]?.start)
      ?? `${dateRef.current}T00:00:00`;
    doSeek(iso);
  };
  const stepBy = (delta: number) => {
    const base = cursorRef.current
      ? new Date(cursorRef.current)
      : new Date(`${dateRef.current}T00:00:00`);
    base.setSeconds(base.getSeconds() + delta);
    const dayStart = new Date(`${dateRef.current}T00:00:00`);
    const dayEnd = new Date(`${dateRef.current}T23:59:59`);
    if (base < dayStart) base.setTime(dayStart.getTime());
    if (base > dayEnd) base.setTime(dayEnd.getTime());
    doSeek(toLocalIso(base));
  };

  const handleRemove = () => {
    const id = playbackIdRef.current;
    if (id) stopPlayback(id).catch(() => {});
    setPlaybackId(null);
    setStreamName(null);
    clearCell(cellId);
  };

  const label = channel != null
    ? `CH${channel}${channelName ? ` · ${channelName}` : ""}`
    : "";

  const empty = deviceId == null || channel == null;

  return (
    <div
      ref={drop as any}
      className={clsx(
        "relative w-full h-full bg-black border border-line overflow-hidden",
        isOver && canDrop && "ring-2 ring-accent",
        focused && !empty && "ring-2 ring-blue-500",
      )}
      onClick={() => onFocus(cellId)}
    >
      {empty ? (
        <div className="absolute inset-0 flex items-center justify-center text-muted text-xs pointer-events-none text-center px-2">
          Arrastra un canal aqui
        </div>
      ) : (
        <>
          {/* El reproductor se monta SOLO cuando hay stream: asi no aparece el
              overlay "Sin senal" del player superpuesto a nuestro estado. */}
          {streamName ? (
            <PlaybackPlayer
              streamName={streamName}
              label={label}
              onDecodeError={transcode ? undefined : onDecodeError}
              onTranscodeFailed={transcode ? onTranscodeFailed : undefined}
              paused={paused}
              playbackRate={rate}
              onTimeUpdate={onTimeUpdate}
              onStreamEnded={onStreamEnded}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-2 pointer-events-none">
              <span className="bg-black/60 text-white px-1.5 py-0.5 rounded text-[11px]">{label}</span>
              {starting ? (
                <span className="flex items-center gap-1 text-blue-200 text-[11px]"><Loader2 className="animate-spin" size={14} /> Conectando...</span>
              ) : searching ? (
                <span className="flex items-center gap-1 text-muted text-[11px]"><Loader2 className="animate-spin" size={14} /> Buscando grabaciones...</span>
              ) : files.length === 0 ? (
                <span className="text-muted text-[11px]">Sin grabaciones este dia</span>
              ) : (
                <span className="flex items-center gap-1 text-muted text-[11px]"><Play size={12} /> Pulsa Reproducir</span>
              )}
            </div>
          )}

          {/* Quitar canal */}
          <button
            className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded hover:bg-red-600/80 z-20"
            onClick={(e) => { e.stopPropagation(); handleRemove(); }}
            title="Quitar canal"
          >
            <X size={11} />
          </button>

          {/* Controles propios (modo independiente) */}
          {showControls && (
            <div
              className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 z-20"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="bg-black/60 text-white p-1 rounded hover:bg-black/80 disabled:opacity-40"
                onClick={() => stepBy(-10)}
                disabled={files.length === 0}
                title="Retroceder 10s"
              >
                <SkipBack size={12} />
              </button>
              {streamName && !paused ? (
                <button className="bg-black/60 text-white p-1 rounded hover:bg-black/80" onClick={() => setPausedState(true)} title="Pausa">
                  <Pause size={12} />
                </button>
              ) : streamName && paused ? (
                <button className="bg-accent text-white p-1 rounded" onClick={() => setPausedState(false)} title="Reanudar">
                  <Play size={12} />
                </button>
              ) : (
                <button className="bg-accent text-white p-1 rounded disabled:opacity-40" onClick={playLocal} disabled={files.length === 0} title="Reproducir">
                  <Play size={12} />
                </button>
              )}
              <button
                className="bg-black/60 text-white p-1 rounded hover:bg-black/80 disabled:opacity-40"
                onClick={() => stepBy(10)}
                disabled={files.length === 0}
                title="Avanzar 10s"
              >
                <SkipForward size={12} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
});
